import { ArrowLeftIcon, Button, Card, Heading, Pane, Paragraph, Text, majorScale } from 'evergreen-ui'
import React, { FC } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import PodcastComingSoon from '../../components/PodcastComingSoon/PodcastComingSoon'
import PodcastPlayer from '../../components/PodcastPlayer/PodcastPlayer'
import { CONSTANTS } from '../../constants'
import { findEpisode, shortsForCase } from '../../data/podcasts'
import { useAppSelector } from '../../hooks'
import { User } from '../../types'
import { generatePageTitle } from '../../utils/generate-page-title'
import { formatEpisodeDate, isPodcastPreviewUser } from '../../utils/podcast-preview'
import styles from './PodcastDetailPage.module.scss'

interface PodcastDetailPageProps {
  flex?: number
}

const PodcastDetailPage: FC<PodcastDetailPageProps> = (props: PodcastDetailPageProps) => {
  const navigate = useNavigate()
  const { podcastId } = useParams()
  const episode = findEpisode(podcastId)
  const shorts = episode ? shortsForCase(episode.id) : []
  const title = generatePageTitle(episode?.title ?? CONSTANTS.TITLES.PODCASTS)

  const user: User | undefined = useAppSelector((state) => state.auth.user)
  const isPreviewUser = isPodcastPreviewUser(user)

  const back = (
    <Button appearance="minimal" iconBefore={ArrowLeftIcon} marginBottom={majorScale(2)} onClick={() => navigate(CONSTANTS.ROUTES.PODCASTS)}>
      All podcasts
    </Button>
  )

  return (
    <Pane
      flex={props.flex ?? 1}
      className={styles.PodcastDetailPage}
      data-testid="PodcastDetailPage"
      padding={majorScale(4)}
      background="tint2"
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {back}
      <Card border="default" padding={majorScale(2)} background="white">
        {!isPreviewUser ? (
          <PodcastComingSoon />
        ) : !episode ? (
          <Pane padding={majorScale(4)}>
            <Heading size={600}>Episode not found</Heading>
            <Text color="muted">This podcast episode doesn't exist or has been removed.</Text>
          </Pane>
        ) : (
          <>
            <PodcastPlayer episode={episode} />
            <Heading size={800} marginTop={majorScale(2)}>
              {episode.title}
            </Heading>
            <Text size={300} color="muted">
              {formatEpisodeDate(episode.publishedAt)} · {episode.durationMinutes} min
            </Text>
            <Paragraph size={500} marginTop={majorScale(1)}>
              {episode.description}
            </Paragraph>
            <Heading size={500} marginTop={majorScale(3)} marginBottom={majorScale(1)}>
              Shorts from this case
            </Heading>
            {shorts.length === 0 ? (
              <Text color="muted">Short clips from this case are coming soon.</Text>
            ) : (
              shorts.map((short) => (
                <Button key={short.id} appearance="minimal" paddingX={0} display="block" onClick={() => navigate(`${CONSTANTS.ROUTES.PODCASTS}/${short.id}`)}>
                  {short.title} · {short.durationMinutes} min
                </Button>
              ))
            )}
          </>
        )}
      </Card>
    </Pane>
  )
}

export default PodcastDetailPage
